<template>
  <section class="news-view">
    <transition name="fade" appear>
      <h2>Band News</h2>
    </transition>
    <article v-for="item in sortedNews" :key="item.id" class="news-article">
      <h3 class="load-hidden">{{ item.title }}</h3>
      <div class="article-body load-hidden">
        <p class="date">{{ formatDate(item.date) }}</p>

        <div class="content">
          <div class="news-content" v-html="joinedContent(item)"></div>

          <div class="images" v-if="item.images?.length">
            <img
              v-for="(img, index) in item.images"
              :key="index"
              :src="`/media/photos/news/${img}`"
              alt="News image"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </article>
  </section>
</template>

<script>
import ScrollReveal from 'scrollreveal';
import newsData from '@/assets/data/news.json';
import { useHead } from '@vueuse/head';

export default {
  name: 'NewsView',
  data() {
    return {
      news: newsData,
    };
  },
  computed: {
    sortedNews() {
      return [...this.news].sort((a, b) => new Date(b.date) - new Date(a.date));
    },
  },
  mounted() {
    useHead({
      title: 'News | NightKill Official',
      meta: [
        {
          name: 'description',
          content:
            'Stay updated on the latest from NightKill — melodic heavy metal from Greece. News, announcements, and more.',
        },
        {
          name: 'keywords',
          content:
            'NightKill news, Greek metal band, heavy metal updates, new releases, band announcements, metal headlines',
        },
        { property: 'og:title', content: 'News | NightKill Official' },
        {
          property: 'og:description',
          content:
            'Catch the latest band updates — NightKill’s news feed includes new releases, tour announcements, and studio drops.',
        },
        {
          property: 'og:image',
          content: 'https://nightkill.gr/metadata/nightkill-logo.png',
        },
        {
          property: 'og:url',
          content: 'https://nightkill.gr/news',
        },
        { property: 'og:type', content: 'website' },
        { property: 'og:site_name', content: 'NightKill Official' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: 'News | NightKill Official' },
        {
          name: 'twitter:description',
          content:
            'News and announcements from NightKill — Greece’s melodic metal band. Stay in the loop.',
        },
        {
          name: 'twitter:image',
          content: 'https://nightkill.gr/metadata/nightkill-logo.png',
        },
      ],
      link: [{ rel: 'canonical', href: 'https://nightkill.gr/news' }],
    });

    this.$nextTick(() => {
      setTimeout(() => {
        const sr = ScrollReveal({
          origin: 'bottom',
          distance: '30px',
          duration: 600,
          easing: 'ease-out',
          cleanup: true,
        });

        sr.reveal('.news-article h3', {
          scale: 0.9,
          interval: 150,
        });

        sr.reveal('.news-article .article-body', {
          delay: 200,
          interval: 150,
        });
      }, 100);
    });
  },
  beforeUnmount() {
    ScrollReveal().destroy();
  },
  methods: {
    formatDate(date) {
      return new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    },
    joinedContent(item) {
      if (Array.isArray(item.content)) {
        return item.content.join('');
      }

      return item.content || '';
    },
  },
};
</script>

<style scoped>
.news-view {
  padding: 0 2rem;
  max-width: 600px;
  margin: 0 auto;
  color: #ddd;
}

h2 {
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: #2c3e50;
  margin-top: 0;
}

.news-article {
  margin-bottom: 3rem;
  border-bottom: 1px solid #444;
  padding-bottom: 2rem;
}

.news-article:last-of-type {
  border-bottom: none;
  padding-bottom: 0;
  margin-bottom: 3rem;
}

.news-article h3 {
  color: #909078;
  font-size: 1.8rem;
}

.date {
  font-size: 1.1rem;
  color: #888;
  margin-bottom: 1rem;
}

.content p {
  margin-bottom: 1rem;
}

:deep(.news-content a) {
  font-weight: 700 !important;
}

:deep(.news-content strong) {
  color: #67667e;
  font-weight: 500;
  margin-right: 3px;
  margin-left: 3px;
}

.images {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1rem;
  margin-bottom: 2rem;
}

.images img {
  max-width: 100%;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.6);
}

/* CSS for the fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}
</style>
